import React, {useRef} from 'react';
import {findDOMNode} from 'react-dom';
import {useIntl} from 'react-intl';
import {
    EDAP_LABEL_NIGHTLY_PRICE_INPUT_MAX,
    EDAP_LABEL_NIGHTLY_PRICE_INPUT_MIN,
    EDAP_LABEL_TOTAL_PRICE_INPUT_MAX,
    EDAP_LABEL_TOTAL_PRICE_INPUT_MIN,
} from '../../utils/constants';
import PriceInput from '../PriceInput/PriceInput';
import './PriceInputs.less';

import type {IntlShape} from 'react-intl';

type OwnProps = {
    filterByTotalPrice?: boolean;
    minPrice: number;
    maxPrice: number;
    maxAllowedPrice: number | null;
    isMax: boolean;
    activeHandleIndex: number | null;
    onChange: (bounds: Array<number | null>) => void;
    currency: string;
    useSeparatorBetweenInputs?: boolean;
};

type StateProps = {
    priceInputValuesAreValid: boolean;
};

type DispatchProps = {
    updatePriceInputValuesValidity: Function;
};

type Props = OwnProps & StateProps & DispatchProps;

export const getMinPriceLabel = (intl: IntlShape): string =>
    intl.formatMessage({
        id: 'min',
        defaultMessage: 'Min',
        description: 'label to identify the minimum price input',
    });

export const getMaxPriceLabel = (intl: IntlShape): string =>
    intl.formatMessage({
        id: 'max',
        defaultMessage: 'Max',
        description: 'label to identify the maximum price input',
    });

export const getMinPriceErrorMessage = (intl: IntlShape): string =>
    intl.formatMessage({
        id: 'priceInputMinMaxErrorMsg',
        defaultMessage: 'Minimum price must be smaller than maximum price',
        description:
            'label to explain min price value must be smaller than maximum price',
    });

export const getMaxPriceErrorMessage = (intl: IntlShape): string =>
    intl.formatMessage({
        id: 'priceInputMaxMinErrorMsg',
        defaultMessage: 'Maximum price must be greater than minimum price',
        description:
            'label to explain max price value must be smaller than minimum price',
    });

export const hasErrorMinPrice = (
    value: number,
    minAllowed?: number,
    maxAllowed?: number
): boolean => typeof maxAllowed === 'number' && value >= maxAllowed;

export const hasErrorMaxPrice = (value: number, minAllowed?: number): boolean =>
    typeof minAllowed === 'number' && value <= minAllowed;

export function PriceInputs({
    activeHandleIndex,
    currency,
    filterByTotalPrice,
    isMax,
    maxAllowedPrice,
    maxPrice,
    minPrice,
    onChange,
    priceInputValuesAreValid,
    updatePriceInputValuesValidity,
    useSeparatorBetweenInputs,
}: Props) {
    const intl = useIntl();

    const onChangeInputValue = (value: number, name: string) => {
        const bounds =
            name === 'minPrice' ? [value, maxPrice] : [minPrice, value];

        onChange(bounds);
    };

    const minPriceInputEl = useRef(null);
    const maxPriceInputEl = useRef(null);

    // eslint-disable-next-line complexity
    const onEnterPress = () => {
        const priceDropdown = document.querySelector('.PriceFilter');

        /* istanbul ignore else */
        if (priceDropdown) {
            const applyButton = priceDropdown.parentElement?.parentElement?.querySelector<
                HTMLElement
            >('.btn-primary');

            const minPriceNode = findDOMNode(minPriceInputEl.current);
            const maxPriceNode = findDOMNode(maxPriceInputEl.current);

            if (
                priceDropdown.contains(minPriceNode) &&
                priceDropdown.contains(maxPriceNode) &&
                applyButton &&
                applyButton.focus
            ) {
                applyButton.focus();
            }
        }
    };

    const handleCheckPriceInputHasError = (hasError: boolean) => {
        updatePriceInputValuesValidity(!hasError);
    };

    const edapLabelMin = filterByTotalPrice
        ? EDAP_LABEL_TOTAL_PRICE_INPUT_MIN
        : EDAP_LABEL_NIGHTLY_PRICE_INPUT_MIN;

    const edapLabelMax = filterByTotalPrice
        ? EDAP_LABEL_TOTAL_PRICE_INPUT_MAX
        : EDAP_LABEL_NIGHTLY_PRICE_INPUT_MAX;

    return (
        <div className="PriceInputs">
            <PriceInput
                id="PriceInput__MinValue"
                name="minPrice"
                isActive={activeHandleIndex === 0}
                value={minPrice}
                minAllowedPrice={0}
                maxAllowedPrice={maxPrice}
                label={getMinPriceLabel(intl)}
                onChange={onChangeInputValue}
                onEnterPress={onEnterPress}
                errorMsg={getMinPriceErrorMessage(intl)}
                hasErrorCallback={hasErrorMinPrice}
                edapLabel={edapLabelMin}
                valueIsValid={priceInputValuesAreValid}
                onCheckHasError={handleCheckPriceInputHasError}
                currency={currency}
                fullLocale={intl.locale}
                ref={minPriceInputEl}
            />
            {useSeparatorBetweenInputs && (
                <div
                    className={'PriceInputs__Separator'}
                    aria-hidden={'true'}
                />
            )}
            <PriceInput
                id="PriceInput__MaxValue"
                name="maxPrice"
                isActive={activeHandleIndex === 1}
                value={maxPrice}
                minAllowedPrice={minPrice}
                maxAllowedPrice={maxAllowedPrice}
                label={getMaxPriceLabel(intl)}
                onChange={onChangeInputValue}
                onEnterPress={onEnterPress}
                errorMsg={getMaxPriceErrorMessage(intl)}
                hasErrorCallback={hasErrorMaxPrice}
                valueIsInfinite={isMax}
                edapLabel={edapLabelMax}
                valueIsValid={priceInputValuesAreValid}
                onCheckHasError={handleCheckPriceInputHasError}
                currency={currency}
                fullLocale={intl.locale}
                ref={maxPriceInputEl}
            />
        </div>
    );
}

export default PriceInputs;
